import { NextSeo } from 'next-seo'
import { Fragment, useCallback } from 'react'
import { AnyObject, Form } from 'react-final-form'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird'
import { faWarning } from '@fortawesome/pro-light-svg-icons/faWarning'
import clsx from 'clsx'
import signCsrfToken from 'server/csrf'

import { DEVELOPER_DASHBOARD_URL } from '@infura/packages.common/constants'
import { Analytics } from '@infura/packages.components/Analytics'
import Notification from '@infura/packages.components/Notification'
import { Button } from '@infura/packages.ui/Button'
import { Text } from '@infura/packages.ui/Text'

import { AuthLayout } from 'components/AuthLayout'
import FieldInput from 'components/FieldInput'
import { Footer } from 'components/loginRegistration/footer'

import { api } from 'lib/api/api'
import handleSubmissionError from 'lib/handleSubmissionError'

const ForgotPasswordForm = ({ onSubmit }: { onSubmit: ({ email, recaptcha }) => Promise<AnyObject> }) => {
  return (
    <>
      <Form
        onSubmit={onSubmit}
        render={({
          handleSubmit,
          modifiedSinceLastSubmit,
          submitError,
          submitSucceeded,
          submitting: formSubmitting
        }) => {
          const submitting = formSubmitting // || captchaSubmitting
          const succeeded = submitSucceeded //  && !error && !captchaSubmitting
          const error = submitError // || captchaError

          return (
            <>
              {succeeded ? (
                <Fragment>
                  <h1 className='font-body text-4xl font-bold'>Reset link sent</h1>
                  <Text as='p' className='mt-6 text-lg'>
                    If there's an account associated with the email address you entered, then we've sent an email to
                    you. Click on the link in the email to reset your password.
                  </Text>
                  <Text as='p' className='mt-6 text-lg'>
                    If you don't see the email, check your junk folder.
                  </Text>
                </Fragment>
              ) : (
                <Fragment>
                  {error ? (
                    <Notification
                      className={clsx(
                        'mx-auto mb-8 w-full p-6',
                        error && !modifiedSinceLastSubmit ? 'visible block' : 'invisible hidden'
                      )}
                      color='alert'
                      size='sm'
                      variant='contained'
                    >
                      <span className='w-full'>
                        <FontAwesomeIcon className='mr-2' icon={faWarning} /> {error}
                      </span>
                    </Notification>
                  ) : null}
                  <>
                    <h1 className='font-body mb-8 text-4xl font-bold'>Reset Password</h1>
                    <Text as='p' className='mb-10 text-lg'>
                      Can’t access your Infura Account? No worries! Enter your email address below and we’ll send you a
                      reset link.
                    </Text>
                  </>
                  <form onSubmit={handleSubmit}>
                    <div>
                      <FieldInput
                        className='mb-6 h-auto px-4 py-3 text-lg'
                        data-testid='email'
                        label='Email'
                        name='email'
                        placeholder='Type your email'
                        required
                        type='email'
                      />
                      <Analytics
                        action='click'
                        event={{
                          location: 'Dashboard Login Page',
                          text: 'Reset Password - Submit',
                          type: 'Button'
                        }}
                      >
                        <Button className='mb-8' data-testid='reset-password' disabled={submitting} type='submit'>
                          {submitting ? (
                            <FontAwesomeIcon className='fa-fw fa-spin text-lg' icon={faSpinnerThird} />
                          ) : (
                            <span className='text-lg'>Submit</span>
                          )}
                        </Button>
                      </Analytics>
                    </div>
                  </form>
                </Fragment>
              )}

              <Footer
                ctaLink='/login'
                ctaText='Login'
                description='Already have login and password?'
                trackingEvent={{
                  clickText: 'Already have login and password? Back to Login',
                  clickUrl: `${DEVELOPER_DASHBOARD_URL}/login`,
                  location: 'Dashboard Login Page',
                  type: 'Text'
                }}
              />
            </>
          )
        }}
      />
    </>
  )
}

function ForgotPassword({ csrfToken }: Readonly<{ csrfToken: string }>) {
  const onSubmit = useCallback(
    async ({ email, recaptcha }) => {
      try {
        await api.sendForgotEmail({ email }, recaptcha || 'blocked', {
          headers: {
            csrf_token: csrfToken
          }
        })
      } catch (e: any) {
        return handleSubmissionError(e)
      }
    },
    [csrfToken]
  )
  return (
    <>
      <AuthLayout>
        <NextSeo
          description="Forgot password. MetaMask Developer provides instant and scalable API access for web3 dapp developers."
          title='Forgot Password | MetaMask Developer'
        />
        <div className='m-auto max-w-3xl px-6 py-8'>
          {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
          <ForgotPasswordForm onSubmit={onSubmit} />
        </div>
      </AuthLayout>
    </>
  )
}

ForgotPassword._noLayout = true

export async function getServerSideProps() {
  return {
    props: { csrfToken: signCsrfToken() }
  }
}

export default ForgotPassword
